<template>
  <div class="text-button-element-wrapper" :class="{ 'dropdown-open': hasOpenDropdown }">
    <!-- Modale de confirmation -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="cancelDelete">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Confirmer la suppression</h3>
          <button class="modal-close" @click="cancelDelete">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p>Êtes-vous sûr de vouloir supprimer ce bouton ?</p>
          <div class="button-to-delete">
            <span class="button-title">{{ buttonToDelete?.title }}</span>
            <span class="button-block">{{ getBlockName(buttonToDelete?.id) }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="modal-cancel" @click="cancelDelete">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            Annuler
          </button>
          <button class="modal-confirm" @click="confirmDelete">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Supprimer
          </button>
        </div>
      </div>
    </div>

    <!-- En-tête du composant -->
    <div class="element-header">
      <span class="element-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <span>Message + Boutons</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Corps du composant -->
    <div class="card">
      <div class="content">
        <!-- Section message -->
        <div class="message-section">
          <!-- Mode affichage -->
          <div v-if="!isEditingMessage" 
               class="message-display"
               @click="startEditMessage">
            <p v-if="element.message" 
               class="message-text"
               v-html="formatMessage(element.message)"></p>
            <p v-else 
               class="message-placeholder">Cliquez pour ajouter un message...</p>
            <span class="char-count" 
                  :class="{'char-count-limit': element.message?.length >= 640}">
              {{ element.message?.length || 0 }}/640
            </span>
          </div>

          <!-- Mode édition -->
          <div v-else class="message-editor">
            <div class="message-input-container">
              <textarea 
                ref="messageEditor"
                v-model="editedMessage"
                class="message-input"
                placeholder="Écrivez votre message ici..."
                @input="handleMessageInput"
                @keydown="handleMessageKeydown"
                @blur="handleBlur"
                maxlength="640"
              ></textarea>
              
              <!-- Toolbar -->
              <div class="editor-toolbar" style="display: flex; gap: 8px; justify-content: flex-end; margin-bottom: 8px; align-items: center;">
                <div class="variable-inserter" style="width: 220px;">
                  <VariableSelector 
                    v-model="selectedVariableToInsert"
                    :variables="variables"
                    placeholder="Insérer une variable..."
                    @change="handleVariableSelect"
                  />
                </div>
              <div class="emoji-button-container" style="position: relative;">
                <button type="button" class="emoji-button" @click.stop.prevent="toggleEmojiPicker">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                    <line x1="9" y1="9" x2="9.01" y2="9"></line>
                    <line x1="15" y1="9" x2="15.01" y2="9"></line>
                  </svg>
                </button>
                
                <!-- Sélecteur d'emoji -->
                <div v-if="showEmojiPicker" class="emoji-picker" @click.stop>
                  <div class="emoji-picker-content">
                    <div class="emoji-category">
                      <button 
                        v-for="emoji in commonEmojis" 
                        :key="emoji"
                        type="button"
                        class="emoji-btn" 
                        @mousedown.prevent="insertEmoji(emoji, $event)"
                      >
                        {{ emoji }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </div>
            <div class="message-footer">
              <span class="char-count" 
                    :class="{'char-count-limit': editedMessage.length >= 640}">
                {{ editedMessage.length }}/640
              </span>
            </div>
            

          </div>
        </div>

        <!-- Section boutons -->
        <div class="buttons-section">
          <!-- Liste des boutons existants -->
          <div class="buttons-list">
            <template v-for="(button, index) in element.buttons || []" :key="index">
              <!-- Mode édition d'un bouton -->
              <div v-if="editingButtonIndex === index" 
                   class="button-edit-form"
                   :class="{ 'dropdown-open': isBlockSelectorOpen || isVariableDropdownOpen }">
                <div class="edit-form-header">
                  <h3>Éditer le bouton</h3>
                  <button class="close-edit-btn" @click="cancelEdit">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>

                <div class="form-group">
                  <label class="form-label">Titre du bouton</label>
                  <div class="input-group">
                    <input 
                      v-model="editButton.title"
                      type="text"
                      class="title-input"
                      placeholder="Ex: En savoir plus"
                      maxlength="20"
                      @input="validateButtonTitle"
                    />
                    <span class="char-count" 
                          :class="{'char-count-limit': editButton.title?.length >= 20}">
                      {{ editButton.title?.length || 0 }}/20
                    </span>
                  </div>
                </div>

                <div class="form-group">
                  <label class="form-label">Redirection (Optionnelle)</label>
                  <BlockSelector
                    :value="selectedBlockId"
                    @input="selectedBlockId = $event"
                    :idBot="idBot"
                    :allowNoBlock="true"
                    @change="onBlockChange"
                    @blocks-loaded="blocks = $event"
                    @open="isBlockSelectorOpen = true"
                    @close="isBlockSelectorOpen = false"
                  />
                </div>

                <!-- Section payload -->
                <div v-if="editButton.hasOwnProperty('payload')" class="payload-section">
                  <div class="payload-header">
                    <span>Variables du payload</span>
                    <button class="remove-payload-btn" @click="removePayload" title="Supprimer le payload">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>

                  <!-- Liste des variables -->
                  <div v-if="payloadVariables.length > 0" class="payload-variables">
                    <div v-for="(variable, varIndex) in payloadVariables" 
                         :key="varIndex"
                         class="variable-item">
                      <span class="variable-content">
                        <span class="variable-name">{{variable.name}}</span>
                        <span class="variable-equals">=</span>
                        <span class="variable-value">{{variable.value}}</span>
                      </span>
                      <button class="delete-variable-btn" 
                              @click="deletePayloadVariable(variable)">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>
                  </div>

                  <!-- Ajout de variable -->
                  <button class="add-variable-btn" 
                          @click="toggleVariableSelector"
                          v-if="!showVariableSelector && payloadVariables.length === 0">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Ajouter une variable
                  </button>

                  <!-- Sélecteur de variable -->
                  <div v-if="showVariableSelector" class="variable-selector">
                    <div class="variable-form">
                      <multiselect
                        v-model="selectedVariable"
                        :options="filteredVariables"
                        :searchable="true"
                        :close-on-select="true"
                        :show-labels="false"
                        open-direction="bottom"
                        placeholder="Sélectionner une variable"
                        label="id"
                        track-by="id"
                        @open="isVariableDropdownOpen = true"
                        @close="isVariableDropdownOpen = false"
                      >
                        <template #option="{ option }">
                          <span class="option-id">{{ option.id }}</span>
                          <span v-if="option.type" class="option-type">({{ option.type }})</span>
                        </template>
                      </multiselect>

                      <input 
                        v-model="variableValue"
                        type="text"
                        class="variable-value-input"
                        placeholder="Valeur"
                      />

                      <div class="variable-actions">
                        <button 
                          class="add-variable-confirm-btn"
                          @click="addVariableToPayload"
                          :disabled="!selectedVariable || !variableValue"
                          :title="isEditingExistingVariable ? 'Modifier la variable' : 'Ajouter la variable'">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </button>
                        <button 
                          class="variable-cancel-btn"
                          @click="toggleVariableSelector"
                          title="Annuler">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Ajout initial de payload -->
                <button 
                  v-else
                  class="add-payload-btn"
                  @click="addPayload">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="16 18 22 12 16 6"></polyline>
                    <polyline points="8 6 2 12 8 18"></polyline>
                  </svg>
                  Ajouter payload
                </button>

                <!-- Boutons d'action -->
                <div class="edit-actions">
                  <button 
                    class="save-btn"
                    @click="saveEditButton(index)"
                    :disabled="!editButton.title || showVariableSelector">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    Valider
                  </button>
                  <button class="cancel-btn" @click="cancelEdit">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    Annuler
                  </button>
                </div>
              </div>

              <!-- Affichage d'un bouton -->
              <div v-else class="button-display" :class="'button-width-' + (element.buttons?.length || 1)">
                <div class="button-wrapper">
                  <button 
                    class="notion-button"
                    @click.stop="startEditButton(index)">
                    <div class="button-content">
                      <span class="button-text">{{ button.title }}</span>
                      <div class="button-metadata">
                        <span class="block-name">{{ getBlockName(button.id) }}</span>
                        <span v-if="button.hasOwnProperty('payload')" 
                              class="payload-badge">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="16 18 22 12 16 6"></polyline>
                            <polyline points="8 6 2 12 8 18"></polyline>
                          </svg>
                        </span>
                      </div>
                    </div>
                  </button>
                  <button 
                    class="delete-button"
                    @click.stop="handleDeleteButton(index)"
                    title="Supprimer ce bouton">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            </template>
          </div>

          <!-- Bouton d'ajout -->
          <button 
            v-if="(!element.buttons || element.buttons.length < 3)"
            class="add-button"
            @click="addButton">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="16"></line>
              <line x1="8" y1="12" x2="16" y2="12"></line>
            </svg>
            <span>Ajouter un bouton</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import BlockSelector from '@/components/common/BlockSelector.vue';
import VariableSelector from '@/components/common/VariableSelector.vue';
import { axiosPeelo } from '@/utils/axios';
import { consoleError } from '@/utils/logger';

export default {
  name: 'TextButtonElement',
  components: {
    Multiselect,
    BlockSelector,
    VariableSelector
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    },
    idBot: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isEditingMessage: false,
      editedMessage: '',
      editingButtonIndex: null,
      editButton: {},
      blocks: [],
      selectedVariable: null,
      variableValue: '',
      isEditingExistingVariable: false,
      currentVariableInPayload: null,
      payloadVariables: [],
      showVariableSelector: false,
      selectedBlockId: '',
      showDeleteConfirm: false,
      buttonToDelete: null,
      buttonIndexToDelete: null,
      showEmojiPicker: false,
      isInsertingEmoji: false,
      selectedVariableToInsert: null,
      cursorPosition: 0,
      isBlockSelectorOpen: false,
      isVariableDropdownOpen: false,
      commonEmojis: ['😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚', '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '☹️', '🙁', '😖', '😞', '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '😡', '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '😇', '🥳', '🥴', '🥺', '🤠', '🤡', '🤥', '🤫', '🤭', '🧐', '🤓', '😈', '👿', '👹', '👺', '💀', '👻', '👽', '🤖', '💩', '👍', '👎', '👌', '✌️', '🤞', '🤘', '🤙', '👈', '👉', '☝️', '👆', '👇', '✋', '🤚', '🖐️', '🖖', '👋', '🤟', '✍️', '👏', '👐', '🙌', '🤲', '🙏', '🤝', '💪', '❤️', '💚', '💙', '💜', '🧡', '💛', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '🎉', '🔥', '✨', '🌟', '👏', '💯']
    };
  },
  computed: {
    filteredVariables() {
      return this.variables?.filter(v => v.type !== 'global') || [];
    },

    hasOpenDropdown() {
      return this.editingButtonIndex !== null || 
             this.showEmojiPicker || 
             this.showVariableSuggestions || 
             this.showVariableSelector ||
             this.isEditingMessage ||
             this.isBlockSelectorOpen ||
             this.isVariableDropdownOpen;
    }
  },
  mounted() {
    this.ensureButtonExists();
    // Créer une version liée pour préserver le contexte this
    this.boundCloseEmojiPicker = this.closeEmojiPickerOnClickOutside.bind(this);
    this.fetchBlocks();
  },
  beforeUnmount() {
    // Nettoyer les écouteurs d'événements lors de la destruction du composant
    document.removeEventListener('click', this.boundCloseEmojiPicker);
  },
  methods: {
    // Gestion du message
    formatMessage(text) {
      if (!text) return '';
      
       
      // Remplacer les sauts de ligne par des <br>
      let formattedText = text.replace(/\n/g, '<br>');
      
      // Mettre en évidence les variables {{variable}} avec un style distinct
      formattedText = formattedText.replace(/{{([^}]+)}}/g, '<span class="variable-highlight">{{$1}}</span>');
      

      
      return formattedText;
    },
    startEditMessage() {
      this.isEditingMessage = true;
      this.editedMessage = this.element.message || '';
      this.$nextTick(() => {
        if (this.$refs.messageEditor) {
          this.$refs.messageEditor.focus();
          // Ajuster la hauteur initiale
          this.$refs.messageEditor.style.height = 'auto';
          this.$refs.messageEditor.style.height = Math.max(60, this.$refs.messageEditor.scrollHeight) + 'px';
        }
      });
    },
    updateCursorPosition() {
      if (!this.isEditingMessage) return;
      const textarea = this.$refs.messageEditor;
      if (!textarea) return;
      this.cursorPosition = textarea.selectionStart;
    },
    handleMessageInput(event) {
      // Ajuster automatiquement la hauteur du textarea
      const textarea = event.target;
      textarea.style.height = 'auto';
      textarea.style.height = Math.max(60, textarea.scrollHeight) + 'px';
      
      // Mettre à jour la position du curseur
      this.cursorPosition = textarea.selectionStart;
    },
    getBlockName(blockId) {
      if (!blockId || blockId === 'NO_BLOCK') return 'Pas de redirection';
      const block = this.blocks.find(b => b.idblock === blockId);
      return block ? (block.blockname || block.idblock) : blockId;
    },
    handleMessageKeydown(event) {
      // Permettre les retours à la ligne avec Entrée
      if (event.key === 'Enter') {
        // Ne pas empêcher le comportement par défaut pour permettre le retour à la ligne
        return;
      }
      
      // Sauvegarder le message avec Ctrl+Enter ou Cmd+Enter
      if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        this.handleBlur();
      }
      
      // Fermer le sélecteur d'emojis avec Escape
      if (event.key === 'Escape') {
        if (this.showEmojiPicker) {
          this.showEmojiPicker = false;
          document.removeEventListener('click', this.boundCloseEmojiPicker);
        } else {
          // Si pas d'emoji picker ouvert, fermer l'édition
          this.handleBlur();
        }
      }
    },
    handleBlur() {
      // Ne pas traiter l'événement blur si nous sommes en train d'insérer un emoji
      if (this.isInsertingEmoji) {
        return;
      }
      
      // Utiliser un délai pour permettre aux événements de clic de se produire d'abord
      setTimeout(() => {
        // Vérifier si le clic est sur un élément lié aux emojis
        const isEmojiRelated = document.activeElement && (
          document.activeElement.closest('.emoji-button') ||
          document.activeElement.closest('.emoji-picker') ||
          document.activeElement.closest('.emoji-btn')
        );
        
        // Vérifier si le clic est sur un élément lié aux variables
        const isVariableRelated = document.activeElement && (
          document.activeElement.closest('.variable-suggestions') ||
          document.activeElement.closest('.variable-suggestion-item')
        );
        
        // Si le clic est sur un élément lié aux emojis ou variables, ne pas sauvegarder
        if (isEmojiRelated || isVariableRelated) {
          // Redonner le focus au textarea
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
          return;
        }
        
        // Sinon, sauvegarder le message
        if (this.editedMessage !== this.element.message) {
          const updatedElement = { ...this.element, message: this.editedMessage };
          if ('text' in this.element) {
            updatedElement.text = this.editedMessage;
          }
          this.$emit('update', updatedElement);
        }
        this.isEditingMessage = false;
        
      }, 100);
    },

    async fetchBlocks() {
      if (!this.idBot) return;
      try {
        const response = await axiosPeelo.get(`/get/list/blocks/${this.idBot}`);
        if (response.data && response.data.list) {
          this.blocks = response.data.list;
        }
      } catch (error) {
        consoleError('Erreur lors du chargement des blocs:', error);
      }
    },

    // Gestion des boutons
    ensureButtonExists() {
      if (!this.element.buttons || this.element.buttons.length === 0) {
        const updatedElement = {
          ...this.element,
          buttons: [{ title: '', id: this.generateRandomId(15) }]
        };
        this.$emit('update', updatedElement);
      }
    },
    addButton() {
      const newButton = { title: '', id: this.generateRandomId(15) };
      const updatedButtons = [...(this.element.buttons || []), newButton];
      this.$emit('update', { ...this.element, buttons: updatedButtons });
      this.startEditButton(updatedButtons.length - 1);
    },
    startEditButton(index) {
      this.editingButtonIndex = index;
      this.editButton = { ...this.element.buttons[index] };
      
      // Assigner directement l'ID du bloc sans validation prématurée
      if (this.editButton.id) {
        this.selectedBlockId = this.editButton.id;
      } else {
        // Nouveau bouton sans ID
        this.editButton.id = this.generateRandomId(15);
        this.selectedBlockId = 'NO_BLOCK';
      }
      
      if (this.editButton.payload) {
        this.extractAllPayloadVariables(this.editButton.payload);
      } else {
        this.payloadVariables = [];
      }
    },
    validateButtonTitle() {
      if (this.editButton.title?.length > 20) {
        this.editButton.title = this.editButton.title.slice(0, 20);
      }
    },
    generateRandomId(length = 15) {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      let result = '';
      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      return result;
    },
    onBlockChange(selectedBlock) {
      if (!selectedBlock) return;
      if (selectedBlock._id === 'NO_BLOCK') {
        const randomId = this.generateRandomId(15);
        this.editButton.id = randomId;
        this.selectedBlockId = 'NO_BLOCK';
      } else {
        this.editButton.id = selectedBlock.idblock;
        this.selectedBlockId = selectedBlock.idblock;
        if (selectedBlock.blockname?.trim() || selectedBlock.displayName?.trim()) {
          this.editButton.title = selectedBlock.blockname || selectedBlock.displayName;
        }
      }
    },
    saveEditButton(index) {
      const updatedButtons = [...this.element.buttons];
      updatedButtons[index] = { ...this.editButton };
      this.$emit('update', { ...this.element, buttons: updatedButtons });
      this.cancelEdit();
    },
    cancelEdit() {
      this.editingButtonIndex = null;
      this.editButton = {};
      this.selectedVariable = null;
      this.variableValue = '';
      this.selectedBlockId = '';
      this.showVariableSelector = false;
      this.isBlockSelectorOpen = false;
      this.isVariableDropdownOpen = false;
    },
    handleDeleteButton(index) {
      this.buttonToDelete = { ...this.element.buttons[index] };
      this.buttonIndexToDelete = index;
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.showDeleteConfirm = false;
      this.buttonToDelete = null;
      this.buttonIndexToDelete = null;
    },
    confirmDelete() {
      const index = this.buttonIndexToDelete;
      const updatedButtons = [...this.element.buttons];
      updatedButtons.splice(index, 1);
      this.$emit('update', { ...this.element, buttons: updatedButtons });
      this.cancelDelete();
    },

    // Gestion du payload
    addPayload() {
      this.editButton.payload = '';
      this.showVariableSelector = true;
    },
    removePayload() {
      delete this.editButton.payload;
      this.payloadVariables = [];
      this.showVariableSelector = false;
    },
    toggleVariableSelector() {
      this.showVariableSelector = !this.showVariableSelector;
      this.selectedVariable = null;
      this.variableValue = '';
    },
    extractAllPayloadVariables(payload) {
      if (!payload) {
        this.payloadVariables = [];
        return;
      }
      
      const variables = [];
      const regex = /{{([^}]+)}}=([^&\s]+)/g;
      let match;
      
      while ((match = regex.exec(payload)) !== null) {
        const variableId = match[1];
        const value = match[2];
        const fullMatch = match[0];
        
        variables.push({
          id: variableId,
          name: variableId,
          value: value,
          fullMatch: fullMatch
        });
      }
      
      this.payloadVariables = variables;
    },
    addVariableToPayload() {
      if (!this.selectedVariable || !this.variableValue) return;
      
      const variableFormat = `{{${this.selectedVariable.id}}}=${this.variableValue}`;
      
      // Toujours écraser le payload existant pour n'en garder qu'un seul
      this.editButton.payload = variableFormat;
      
      this.extractAllPayloadVariables(this.editButton.payload);
      this.selectedVariable = null;
      this.variableValue = '';
      this.isEditingExistingVariable = false;
      this.currentVariableInPayload = null;
      this.showVariableSelector = false;
    },
    deletePayloadVariable(variable) {
      if (!this.editButton.payload) return;
      
      this.editButton.payload = this.editButton.payload
        .replace(variable.fullMatch, '')
        .replace(/^&|&$/g, '')
        .replace(/&&/g, '&')
        .trim();
      
      this.extractAllPayloadVariables(this.editButton.payload);
      
      if (!this.editButton.payload) {
        delete this.editButton.payload;
      }
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
      
      if (this.showEmojiPicker) {
        this.$nextTick(() => {
          setTimeout(() => {
            document.addEventListener('click', this.boundCloseEmojiPicker);
          }, 0);
        });
      } else {
        document.removeEventListener('click', this.boundCloseEmojiPicker);
      }
      
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },
    insertEmoji(emoji, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      this.isInsertingEmoji = true;
      
      const textarea = this.$refs.messageEditor;
      if (textarea) {
        const cursorPosition = textarea.selectionStart;
        const textBefore = this.editedMessage.substring(0, cursorPosition);
        const textAfter = this.editedMessage.substring(cursorPosition);
        
        this.editedMessage = textBefore + emoji + textAfter;
        
        // Ne pas fermer le picker pour permettre la sélection multiple
        // this.showEmojiPicker = false;
        // document.removeEventListener('click', this.boundCloseEmojiPicker);
        
        this.$nextTick(() => {
          setTimeout(() => {
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              this.$refs.messageEditor.focus();
              const newPosition = cursorPosition + emoji.length;
              this.$refs.messageEditor.selectionStart = newPosition;
              this.$refs.messageEditor.selectionEnd = newPosition;
              this.handleMessageInput({ target: textarea });
            }
          }, 50); // Réduit le délai pour une meilleure réactivité
        });
      }
    },
    closeEmojiPickerOnClickOutside(event) {
      if (!this.showEmojiPicker) return;
      
      if (event.target.closest('.emoji-picker') || 
          event.target.closest('.emoji-button') || 
          event.target.closest('.emoji')) {
        return;
      }
      
      this.showEmojiPicker = false;
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },
    handleVariableSelect(variableId) {
      if (!variableId) return;
      
      this.isInsertingEmoji = true; 
      
      const variableText = `{{${variableId}}}`;
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const textAfterCursor = this.editedMessage.substring(this.cursorPosition);
      
      this.editedMessage = textBeforeCursor + variableText + textAfterCursor;
      
      this.selectedVariableToInsert = null;
      
      this.$nextTick(() => {
        setTimeout(() => {
          this.isInsertingEmoji = false;
          if (this.$refs.messageEditor) {
            const newCursorPos = this.cursorPosition + variableText.length;
            this.$refs.messageEditor.focus();
            this.$refs.messageEditor.selectionStart = newCursorPos;
            this.$refs.messageEditor.selectionEnd = newCursorPos;
            this.cursorPosition = newCursorPos;
          }
        }, 100);
      });
    },
    insertVariable(variable, event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      this.isInsertingEmoji = true; // On utilise la même variable que pour les emojis
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const textAfterCursor = this.editedMessage.substring(this.cursorPosition);
      
      // Trouver où commence la variable en cours de saisie
      const match = textBeforeCursor.match(/{{([^}]*)$/); 
      
      if (match) {
        const startPos = textBeforeCursor.lastIndexOf('{{');
        const variableText = `{{${variable.id}}}`;
        
        // Remplacer le début de la variable par la variable complète
        this.editedMessage = textBeforeCursor.substring(0, startPos) + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur après la variable insérée
        this.$nextTick(() => {
          setTimeout(() => {
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = startPos + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
              this.handleMessageInput({ target: this.$refs.messageEditor });
            }
          }, 50);
        });
      } else {
        // Si pas de variable en cours, insérer à la position du curseur
        const variableText = `{{${variable.id}}}`;
        this.editedMessage = textBeforeCursor + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur
        this.$nextTick(() => {
          setTimeout(() => {
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = this.cursorPosition + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
              this.handleMessageInput({ target: this.$refs.messageEditor });
            }
          }, 50);
        });
      }
      
      
    }
  }
};
</script>

<style scoped>
@import 'vue-multiselect/dist/vue-multiselect.css';

.text-button-element-wrapper {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.text-button-element-wrapper:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

/* Header styles */
.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028; /* Peelo Dark Green */
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #daffd1; /* Peelo Mint */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.element-type-badge svg {
  color: #daffd1;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 255, 209, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #daffd1;
  border-color: #daffd1;
  transform: scale(1.05);
}

.action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

/* Message section */
.message-section {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.message-display {
  cursor: pointer;
  transition: all 0.2s ease;
}

.message-display:hover {
  background: rgba(59, 130, 246, 0.02);
  border-radius: 6px;
}

.message-text,
.message-placeholder {
  margin: 0;
  padding: 12px;
  min-height: 60px;
  height: auto;
  line-height: 1.5;
  color: #1f2937;
  font-family: inherit;
  font-size: 14px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  word-wrap: break-word;
  white-space: pre-wrap;
  display: block;
  box-sizing: border-box;
}

/* Style pour mettre en évidence les variables */
.text-button-element-wrapper .message-text .variable-highlight {
  background: #daffd1 !important;
  color: #143028 !important;
  padding: 4px 10px !important;
  border-radius: 16px !important;
  font-weight: 600 !important;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  font-size: 0.8em !important;
  display: inline-block !important;
  margin: 0 3px 2px 0 !important;
  vertical-align: middle !important;
  box-shadow: 0 2px 4px rgba(20, 48, 40, 0.1) !important;
  border: 1px solid rgba(20, 48, 40, 0.1) !important;
  white-space: nowrap !important;
  text-shadow: none !important;
  position: relative !important;
}

.text-button-element-wrapper .message-text .variable-highlight::before {
  content: '●' !important;
  position: absolute !important;
  left: 4px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  color: #143028 !important;
  font-size: 0.6em !important;
  opacity: 0.8 !important;
}

.text-button-element-wrapper .message-text .variable-highlight:hover {
  background: #143028 !important;
  color: #daffd1 !important;
  border-color: #143028 !important;
  transform: translateY(-1px) scale(1.05) !important;
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.15) !important;
}

.text-button-element-wrapper .message-text .variable-highlight:active {
  transform: translateY(0) scale(1.02) !important;
}

.message-placeholder {
  color: #9ca3af;
  cursor: pointer;
  font-style: italic;
}

.message-editor {
  position: relative;
}

.message-input-container {
  position: relative;
  display: flex;
  align-items: flex-start;
}

.message-input {
  width: 100%;
  min-height: 60px;
  height: auto;
  padding: 12px 50px 12px 12px;
  border: 1px solid transparent;
  border-radius: 6px;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  color: #1f2937;
  background: transparent;
  outline: none;
  transition: all 0.2s ease;
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow: hidden;
  display: block;
  box-sizing: border-box;
}

.message-input:focus {
  border-color: transparent;
  box-shadow: none;
}

.emoji-button-container {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 10;
}

.emoji-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.emoji-button:hover {
  background: #daffd1;
  color: #143028;
  transform: scale(1.1);
}

.emoji-picker {
  position: absolute;
  top: 100%;
  right: 0;
  width: 300px;
  max-height: 250px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 8px;
  animation: fadeIn 0.2s ease;
  overflow-y: auto;
  transform-origin: top right;
  border: 1px solid #e5e7eb;
}

.emoji-picker-content {
  padding: 12px;
}

.emoji-category {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.emoji-btn {
  cursor: pointer;
  font-size: 18px;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s ease;
  background: none;
  border: none;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
}

.emoji-btn:hover {
  background: #f3f4f6;
  transform: scale(1.1);
  border-radius: 8px;
}

.message-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 0;
}

.char-count {
  display: block;
  text-align: right;
  font-size: 0.75rem;
  color: #6b7280;
  margin-left: 4px;
}

.char-count-limit {
  color: #ef4444;
  font-weight: 500;
}

@keyframes fadeIn {
  from { 
    opacity: 0; 
    transform: translateY(-10px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
}

/* Buttons section */
.buttons-section {
  padding: 16px;
}

.buttons-list {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  margin-bottom: 16px;
  width: 100%;
  min-height: 76px;
  position: relative;
}

.button-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  position: relative;
}

.button-width-1 {
  width: 100%;
}

.button-width-2 {
  width: calc(50% - 4px);
}

.button-width-3 {
  width: calc(33.333% - 5.333px);
}

.button-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 76px;
  display: flex;
}

.notion-button {
  background: #daffd182; /* Transparent Peelo Mint */
  border: 1px solid rgba(20, 48, 40, 0.1);
  border-radius: 12px;
  padding: 12px 16px;
  color: #143028; /* Peelo Dark Green */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  height: 100%;
  font-size: 0.95rem;
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.05);
}

.notion-button:hover {
  background: #c5f9b8;
  border-color: rgba(20, 48, 40, 0.2);
  box-shadow: 0 6px 15px rgba(20, 48, 40, 0.08);
  transform: translateY(-1px);
}

.button-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.button-metadata {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.block-name {
  font-size: 0.75rem;
  color: #143028;
  opacity: 0.6;
  font-weight: 500;
}

.whatsapp-button:hover {
  background: #d1d5db;
}

.button-text {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.payload-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #143028;
  color: #daffd1;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.delete-button {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  border-radius: 3px;
  border: none;
  background: transparent;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  opacity: 0;
  padding: 0;
  font-size: 0.75rem;
  z-index: 2;
}

.button-wrapper:hover .delete-button {
  opacity: 1;
}

.delete-button:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.add-button {
  width: 100%;
  padding: 10px;
  background: transparent;
  border: 1px dashed rgba(20, 48, 40, 0.2);
  border-radius: 8px;
  color: #143028;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  font-weight: 500;
  font-size: 0.9rem;
}

.add-button:hover {
  background: rgba(218, 255, 209, 0.3);
  border-color: #143028;
  color: #143028;
}

/* Remove payload button */
.remove-payload-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: #9ca3af;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
}

.remove-payload-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Add payload button */
.add-payload-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 8px;
  background: white;
  color: #143028;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;
}

.add-payload-btn:hover {
  background: rgba(218, 255, 209, 0.2);
  border-color: #143028;
}

.add-payload-btn i {
  font-size: 0.85rem;
}

.add-variable-confirm-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #143028;
  color: #daffd1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.add-variable-confirm-btn:hover:not(:disabled) {
  background: #042b26;
  transform: scale(1.05);
}

.add-variable-confirm-btn:disabled {
  background: rgba(20, 48, 40, 0.1);
  color: rgba(20, 48, 40, 0.3);
  cursor: not-allowed;
}

.variable-actions {
  display: flex;
  gap: 8px;
}

.variable-cancel-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.variable-cancel-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.05);
}

/* Button edit form */
.button-edit-form {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 16px;
  padding: 20px;
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  z-index: 999;
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.15);
  margin-top: 8px;
  border: 1px solid rgba(20, 48, 40, 0.15);
}

.button-edit-form.dropdown-open {
  overflow: visible !important;
}

/* Style de la scrollbar pour Webkit (Chrome, Safari, etc.) */
.button-edit-form::-webkit-scrollbar {
  width: 8px;
}

.button-edit-form::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.button-edit-form::-webkit-scrollbar-thumb {
  background: #94a3b8;
  border-radius: 4px;
}

.button-edit-form::-webkit-scrollbar-thumb:hover {
  background: #64748b;
}

.edit-form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  position: sticky;
  top: 0;
  background: white;
  padding: 8px 0;
  z-index: 1;
  color: #143028;
}

.edit-form-header h3 {
  color: #143028;
  font-weight: 600;
  margin: 0;
}

.close-edit-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: rgba(20, 48, 40, 0.05);
  color: #143028;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.close-edit-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.block-select,
.title-input {
  width: 100%;
  padding: 12px 16px;
  padding-right: 60px; /* Space for char count */
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 12px;
  background: #f8fafc;
  outline: none;
  font-size: 14.5px;
  color: #143028;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
}

.title-input:hover {
  background: #ffffff;
  border-color: rgba(20, 48, 40, 0.3);
}

.block-select:focus,
.title-input:focus {
  border-color: #2c463f;
  background: #ffffff;
  box-shadow: 0 4px 15px rgba(44, 70, 63, 0.1), 0 0 0 3px rgba(44, 70, 63, 0.2);
}

.input-group .char-count {
  position: absolute;
  right: 12px;
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  background: transparent;
  padding: 0;
  border: none;
}

/* Payload section */
.payload-section {
  margin-top: 16px;
  padding: 16px;
  background: rgba(20, 48, 40, 0.02);
  border: 1px solid rgba(20, 48, 40, 0.08);
  border-radius: 8px;
}

.payload-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.payload-header span {
  font-weight: 600;
  color: #143028;
  font-size: 0.9rem;
}

.payload-variables {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.variable-item {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #daffd1;
  border: 1px solid rgba(20, 48, 40, 0.15);
  border-radius: 6px;
  padding: 4px 10px;
}

.variable-content {
  display: flex;
  align-items: center;
  gap: 4px;
}

.variable-name {
  color: #143028;
  font-weight: 600;
  font-family: monospace;
}

.variable-equals {
  color: rgba(20, 48, 40, 0.6);
}

.variable-value {
  color: #143028;
  font-weight: 500;
}

.delete-variable-btn {
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  color: rgba(20, 48, 40, 0.6);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-variable-btn:hover {
  color: #ef4444;
}

/* Variable selector */
.add-variable-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px 12px;
  border: 1px dashed rgba(20, 48, 40, 0.2);
  border-radius: 8px;
  background: transparent;
  color: #143028;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 8px;
}

.add-variable-btn:hover {
  border-color: #143028;
  color: #143028;
  background: rgba(218, 255, 209, 0.3);
}

.add-variable-btn i {
  font-size: 0.85rem;
}

.variable-selector {
  margin-top: 12px;
}

.variable-form {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.variable-value-input {
  flex: 1;
  padding: 8px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  outline: none;
  font-size: 0.9rem;
  color: #1e293b;
  transition: all 0.2s ease;
}

.variable-value-input:focus {
  border-color: #143028;
  box-shadow: 0 0 0 3px rgba(218, 255, 209, 0.4);
}

/* Buttons */
.edit-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}

.save-btn,
.cancel-btn {
  flex: 1;
  padding: 8px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.save-btn {
  background: #143028;
  border: none;
  color: #daffd1;
  font-weight: 600;
}

.save-btn:hover:not(:disabled) {
  background: #042b26;
}

.save-btn:disabled {
  background: rgba(20, 48, 40, 0.1);
  color: rgba(20, 48, 40, 0.3);
  cursor: not-allowed;
}

.cancel-btn {
  background: white;
  border: 1px solid rgba(20, 48, 40, 0.2);
  color: #143028;
  font-weight: 500;
}

.cancel-btn:hover {
  background: rgba(20, 48, 40, 0.05);
  border-color: #143028;
}

/* Vue Multiselect customization */
.multiselect {
  min-height: 38px;
}

::v-deep .multiselect__tags {
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 40px 0 8px;
  transition: all 0.2s ease;
}

::v-deep .multiselect__tags:hover {
  border-color: rgba(20, 48, 40, 0.3);
}

::v-deep .multiselect--active .multiselect__tags {
  border-color: #143028;
  box-shadow: 0 0 0 3px rgba(218, 255, 209, 0.4);
}

::v-deep .multiselect__option--highlight {
  background: #daffd1;
  color: #143028;
}

::v-deep .multiselect__option--selected {
  background: rgba(218, 255, 209, 0.5);
  color: #143028;
  font-weight: 600;
}

/* Block multiselect specific styles */
.block-multiselect {
  margin-bottom: 12px;
}

.block-multiselect .multiselect__tags {
  border-color: #e2e8f0;
}

.option-special {
  color: #143028;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.option-special::before {
  content: '✨';
  font-size: 0.9em;
}

.option-separator {
  color: #d1d5db;
  text-align: center;
  font-weight: 300;
  font-size: 0.85em;
  pointer-events: none;
  user-select: none;
}

.option-block {
  color: #1f2937;
}

.variable-suggestions {
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(20, 48, 40, 0.15);
  z-index: 1000;
  margin-bottom: 8px;
  animation: fadeIn 0.2s ease;
  border: 1.5px solid #143028;
  max-height: 200px;
  overflow-y: auto;
}

.variable-suggestions-content {
  padding: 12px;
}

.variable-category {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.variable-suggestion-item {
  padding: 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #daffd1;
  border: 1px solid rgba(20, 48, 40, 0.15);
  outline: none;
  border-radius: 16px;
  font-size: 0.875rem;
  color: #143028;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  font-weight: 500;
  box-shadow: 0 2px 4px rgba(20, 48, 40, 0.05);
}

.variable-suggestion-item:hover {
  background: #143028;
  color: #daffd1;
  border-color: #143028;
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(20, 48, 40, 0.15);
}

.variable-suggestion-item:focus {
  background: #143028;
  color: #daffd1;
  border-color: #143028;
  outline: 2px solid #daffd1;
  outline-offset: 2px;
}
</style>